import { useCallback, useEffect, useState } from "react";
import { Loader2, Users, Info } from "lucide-react";
import api from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { PageHeader } from "../components/common/PageHeader";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";

function monthDefault() {
  const t = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  return { from: iso(new Date(t.getFullYear(), t.getMonth(), 1)), to: iso(new Date(t.getFullYear(), t.getMonth() + 1, 0)) };
}

export default function MixedOwnerAudit() {
  const { ctx } = useWorkspace();
  const pid = ctx.project?.id;
  const [range, setRange] = useState(monthDefault());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/data-entry/mixed-owner-audit", {
      params: { ...(pid ? { project_id: pid } : {}), date_from: range.from, date_to: range.to, page, page_size: 50 },
    }).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [pid, range.from, range.to, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [pid, range.from, range.to]);

  const items = data?.items || [];
  const totalPages = Math.max(1, Math.ceil((data?.total || 0) / 50));

  return (
    <div data-testid="mixed-owner-audit">
      <PageHeader title="Mixed-Owner Completions" subtitle="Completed entries whose materials were acknowledged by more than one field user" />

      <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
        <span className="text-xs text-slate-500">From</span>
        <Input data-testid="moa-from" type="date" value={range.from} max={range.to} onChange={(e) => setRange((s) => ({ ...s, from: e.target.value }))} className="h-9 w-40" />
        <span className="text-xs text-slate-500">To</span>
        <Input data-testid="moa-to" type="date" value={range.to} min={range.from} onChange={(e) => setRange((s) => ({ ...s, to: e.target.value }))} className="h-9 w-40" />
      </div>

      <div className="mb-3 flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800" data-testid="moa-note">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>These entries were completed with materials (e.g. Meter + Seal) acknowledged by different field users. This is now allowed — logged here for review.</span>
      </div>

      {loading ? (
        <div className="grid place-items-center rounded-xl border border-slate-200 bg-white py-12"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 py-10 text-center text-sm text-slate-400">No mixed-owner completions in this range</div>
      ) : (
        <>
          <div className="mb-2 text-xs text-slate-500" data-testid="moa-count">{data.total} entr{data.total === 1 ? "y" : "ies"}</div>
          <div className="space-y-3">
            {items.map((r) => (
              <div key={r.id} data-testid={`moa-row-${r.entry_number}`} className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <span className="font-mono text-sm font-semibold text-slate-800">{r.entry_number}</span>
                    <span className="ml-2 text-xs text-slate-400">{r.project_name} · {r.form_type} · Consumer {r.consumer_number || "—"}</span>
                  </div>
                  <Badge className="bg-amber-100 text-amber-800 border border-amber-300"><Users className="mr-1 h-3 w-3" />{r.owner_count} field users</Badge>
                </div>
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead><tr className="text-slate-400"><th className="py-1 pr-3 font-medium">Role</th><th className="py-1 pr-3 font-medium">Material</th><th className="py-1 pr-3 font-medium">Acknowledged by</th></tr></thead>
                    <tbody>
                      {r.materials.map((m, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="py-1 pr-3"><Badge variant="secondary">{m.role}</Badge></td>
                          <td className="py-1 pr-3 font-mono text-slate-700">{m.serial || m.material_number || "—"}{m.type_label ? ` (${m.type_label})` : ""}</td>
                          <td className="py-1 pr-3 text-slate-700">{m.field_user}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-2 text-[11px] text-slate-400">Completed by {r.completed_by} · {(r.completed_at || "").slice(0, 19).replace("T", " ")}</div>
              </div>
            ))}
          </div>
          {totalPages > 1 && (
            <div className="mt-3 flex items-center justify-end gap-2">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="moa-prev">Prev</Button>
              <span className="text-sm text-slate-600">Page {page} / {totalPages}</span>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="moa-next">Next</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
